import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Header from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  BookOpen, Clock, BarChart3, ChevronRight, Layers, Play, Award, CheckCircle,
  GraduationCap, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { resolveUploadUrl } from '../utils/media';

const CoursesPage = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    level: '',
    category: '',
  });

  const canCreateCourse = user?.role === 'trainer' || user?.role === 'cfa_admin';

  const getAvatarUrl = (avatarUrl) => resolveUploadUrl(avatarUrl);

  const fetchCourses = useCallback(async () => {
    try {
      const response = await api.get('/content/courses');
      const raw = Array.isArray(response.data) ? response.data : [];
      // Students: published only. Trainers/admins: include drafts they manage.
      const coursesData =
        user?.role === 'student' ? raw.filter((c) => c.is_published) : raw;
      setCourses(coursesData);

      if (user?.role === 'student') {
        const progressData = {};
        for (const course of coursesData) {
          try {
            const progressResponse = await api.get(`/content/student/course-progress/${course.id}`);
            progressData[course.id] = progressResponse.data;
          } catch (err) {
            progressData[course.id] = { progress_percent: 0, completed_lessons: 0, total_lessons: 0 };
          }
        }
        setProgress(progressData);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Erreur lors du chargement des cours');
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const getStatusBadge = (course, progressPercent) => {
    if (user?.role === 'trainer' || user?.role === 'cfa_admin') {
      return course.is_published ? (
        <Badge className="bg-emerald-100 text-emerald-700">Publié</Badge>
      ) : (
        <Badge variant="secondary">Brouillon</Badge>
      );
    }
    if (progressPercent === 0) {
      return <Badge variant="secondary">Non commencé</Badge>;
    } else if (progressPercent === 100) {
      return <Badge className="bg-emerald-100 text-emerald-700">Terminé</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-700">En cours</Badge>;
  };

  const handleCourseClick = (course) => {
    if (user?.role === 'student') {
      navigate(`/student/courses/${course.id}`);
    } else if (user?.role === 'trainer') {
      navigate(`/admin/courses/${course.id}/edit`);
    } else {
      navigate(`/admin/courses/${course.id}/edit`);
    }
  };

  const resetCreateForm = () => {
    setFormData({ name: '', description: '', level: '', category: '' });
  };

  const handleCreateCourse = async () => {
    if (!formData.name.trim()) {
      toast.error('Le nom du cours est requis');
      return;
    }

    setCreating(true);
    try {
      const response = await api.post('/content/courses', {
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
        level: formData.level || null,
        category: formData.category?.trim() || null,
        is_published: false,
      });
      toast.success('Cours créé avec succès');
      setShowCreateDialog(false);
      resetCreateForm();
      navigate(`/admin/courses/${response.data.id}/edit`);
    } catch (error) {
      console.error('Error creating course:', error);
      const detail = error.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Erreur lors de la création du cours'
      );
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  const formatCourseDuration = (course) => {
    if (course.duration_calculated?.total_minutes > 0) {
      return course.duration_calculated.formatted;
    }
    if (course.duration_hours) return `${course.duration_hours}h`;
    return 'N/A';
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 overflow-x-hidden">
      <Header title="Mes Cours" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto w-full px-3 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 pb-24 sm:pb-8">
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-white mb-1 sm:mb-2">
              Mes Cours
            </h2>
            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">
              {courses.length} cours disponible(s)
            </p>
          </div>
          {canCreateCourse && (
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-violet-600 hover:bg-violet-700 w-full sm:w-auto shrink-0"
              data-testid="create-course-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau cours
            </Button>
          )}
        </div>

        {courses.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {courses.map((course) => {
              const courseProgress = progress[course.id] || { progress_percent: 0 };
              const progressPercent = courseProgress.progress_percent || 0;

              return (
                <Card
                  key={course.id}
                  className="overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer flex flex-col"
                  data-testid={`course-card-${course.id}`}
                  onClick={() => handleCourseClick(course)}
                >
                  {/* Course Image */}
                  <div className="h-36 sm:h-44 md:h-48 bg-gradient-to-br from-violet-600 via-indigo-600 to-blue-600 relative overflow-hidden">
                    {course.cover_image_url ? (
                      <img src={course.cover_image_url} alt={course.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 text-white/50" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    
                    {/* Status Badge */}
                    <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
                      {getStatusBadge(course, progressPercent)}
                    </div>
                    
                    {/* Completed indicator */}
                    {user?.role === 'student' && progressPercent === 100 && (
                      <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                          <Award className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <CardContent className="p-4 sm:p-6 flex flex-col flex-1">
                    <h3 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white mb-2 group-hover:text-violet-600 transition-colors line-clamp-2 break-words">
                      {course.name}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-4">
                      {course.description || 'Découvrez ce cours de formation'}
                    </p>
                    
                    {/* Course Info */}
                    <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400 min-w-0">
                        <Layers className="w-4 h-4 text-violet-600 shrink-0" />
                        <span className="truncate">{course.module_count || 0} modules</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400 min-w-0">
                        <Clock className="w-4 h-4 text-violet-600 shrink-0" />
                        <span className="truncate">{formatCourseDuration(course)}</span>
                      </div>
                    </div>

                    {/* Trainer Info */}
                    {course.trainer_name && (
                      <div className="flex items-center gap-3 mb-4 p-2.5 sm:p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center">
                          {course.trainer_avatar_url ? (
                            <img 
                              src={getAvatarUrl(course.trainer_avatar_url)} 
                              alt={course.trainer_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <GraduationCap className="w-5 h-5 text-white" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                            {course.trainer_name}
                          </p>
                          {course.trainer_specialization && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                              {course.trainer_specialization}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Progress Bar */}
                    {user?.role === 'student' && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 min-w-0">
                            <BarChart3 className="w-4 h-4 text-violet-600 shrink-0" />
                            <span className="truncate">Progression</span>
                          </div>
                          <span className="text-sm font-semibold text-violet-600 shrink-0">{progressPercent}%</span>
                        </div>
                        <Progress value={progressPercent} className="h-2" />
                      </div>
                    )}

                    {/* Action Button */}
                    <Button 
                      className="w-full mt-auto bg-violet-600 hover:bg-violet-700"
                      onClick={(e) => { e.stopPropagation(); handleCourseClick(course); }}
                    >
                      {user?.role === 'trainer' || user?.role === 'cfa_admin' ? (
                        <>
                          <ChevronRight className="w-4 h-4 mr-2" />
                          Gérer le contenu
                        </>
                      ) : progressPercent === 0 ? (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Commencer
                        </>
                      ) : progressPercent === 100 ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Revoir
                        </>
                      ) : (
                        <>
                          <ChevronRight className="w-4 h-4 mr-2" />
                          Continuer
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-8 sm:p-12 text-center">
              <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">Aucun cours disponible</h3>
              <p className="text-slate-400 mb-4 text-sm sm:text-base">
                {canCreateCourse
                  ? 'Créez votre premier cours pour commencer à ajouter du contenu pédagogique.'
                  : 'Les cours seront bientôt disponibles'}
              </p>
              {canCreateCourse && (
                <Button
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-violet-600 hover:bg-violet-700 w-full sm:w-auto"
                  data-testid="create-course-empty-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un cours
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create course dialog (trainers & admins) */}
      <Dialog
        open={showCreateDialog}
        onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) resetCreateForm();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-violet-600" />
              Nouveau cours
            </DialogTitle>
            <DialogDescription>
              Créez un cours, puis ajoutez modules et leçons dans l&apos;éditeur.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label>Nom du cours *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Comptabilité générale"
                data-testid="course-name-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Objectifs et contenu du cours..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Niveau</Label>
                <select
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-800"
                >
                  <option value="">Non précisé</option>
                  <option value="beginner">Débutant</option>
                  <option value="intermediate">Intermédiaire</option>
                  <option value="advanced">Avancé</option>
                </select>
              </div>
              <div>
                <Label>Catégorie</Label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Ex: Gestion"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                resetCreateForm();
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreateCourse}
              disabled={creating}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="submit-create-course"
            >
              {creating ? 'Création...' : 'Créer et éditer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoursesPage;
